import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { SmagAccountService } from '../../../framework/auth/services/index';

@Component({
  selector: 'devices-window',
  templateUrl: './deviceswindow.component.html',
  styleUrls: ['./deviceswindow.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DeviceswindowComponent implements OnChanges, OnDestroy {

  @Input() public opened: boolean = false;
  @Input() public devicesUrl: string = '';
  @Output() public DialogClosed = new EventEmitter<boolean>();

  @ViewChild('devicesMount') private devicesMount: ElementRef;
  private mountRequestVersion = 0;
  // Mémoriser l'état du scroll avant de le
  // verrouiller, puis le restaurer sans modifier le comportement global de la page.
  private bodyOverflowBeforeLock: string | null = null;
  private htmlOverflowBeforeLock: string | null = null;

  constructor(private smagAccountService: SmagAccountService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['opened']) {
      if (changes['opened'].currentValue === true) {
        // Lors de l'ouverture, on bloque le scroll du document pour empêcher la page
        // de défiler derrière la fenêtre modale.
        this.lockDocumentScroll();
        this.loadAssetsAndMount();
      } else {
        // À la fermeture, on restaure instantanément l'état précédent du scroll.
        this.cancelPendingMount();
        this.unlockDocumentScroll();
      }
    }
  }

  ngOnDestroy(): void {
    // Sécurité supplémentaire : si le composant est détruit pendant l'ouverture,
    // on remet le scroll dans son état initial.
    this.cancelPendingMount();
    this.unlockDocumentScroll();
  }

  public close(): void {
    this.opened = false;
    // La fermeture explicite du widget réactive le scroll du document avant d'émettre
    // l'événement de fermeture.
    this.cancelPendingMount();
    this.unlockDocumentScroll();
    this.DialogClosed.emit(this.opened);
  }

  private cancelPendingMount(): void {
    this.mountRequestVersion += 1;
  }

  // Empêcher le document de
  // défiler pendant l'ouverture de la fenêtre et corriger le bug du bandeau supérieur.
  private lockDocumentScroll(): void {
    if (typeof document === 'undefined' || this.bodyOverflowBeforeLock !== null) {
      return;
    }

    this.bodyOverflowBeforeLock = document.body.style.overflow;
    this.htmlOverflowBeforeLock = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  // Restaurer le scroll de la page à son état initial après la fermeture du composant.
  private unlockDocumentScroll(): void {
    if (typeof document === 'undefined' || this.bodyOverflowBeforeLock === null) {
      return;
    }

    document.body.style.overflow = this.bodyOverflowBeforeLock;
    document.documentElement.style.overflow = this.htmlOverflowBeforeLock ?? '';
    this.bodyOverflowBeforeLock = null;
    this.htmlOverflowBeforeLock = null;
  }

  private loadAssetsAndMount(): void {
    const mountRequestVersion = ++this.mountRequestVersion;
    this.loadScript().then(() => {
      setTimeout(() => {
        if (!this.opened || mountRequestVersion !== this.mountRequestVersion) {
          return;
        }
        this.mountComponent();
      });
    });
  }

  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector('script[data-devices-bundle]')) { resolve(); return; }
      const script = document.createElement('script');
      script.setAttribute('data-devices-bundle', '');
      script.src = this.devicesUrl;
      script.onload = () => resolve();
      script.onerror = () => reject();
      document.head.appendChild(script);
    });
  }

  /**
   * Comme SmagButton (cf. smag-account.service.ts), le module Devices s'attache en Shadow
   * DOM (mode "open") via mountSmagLibVueModule, qui injecte lui-même son CSS (inliné dans
   * le bundle UMD par le toolchain smagLib) dans ce shadowRoot : aucune récupération ni
   * injection manuelle de CSS n'est nécessaire côté hôte.
   */
  private mountComponent(): void {
    if (!this.devicesMount?.nativeElement) return;
    const smagModule = (window as any).SmagModulePrecisionAg;
    if (!smagModule || typeof smagModule.Devices !== 'function') return;
    const container = this.devicesMount.nativeElement;
    container.innerHTML = '';
    const smagUser = this.smagAccountService.getSmagUser();
    const token = smagUser ? smagUser.smag_token : '';
    smagModule.Devices(container, {
      data() { return { token }; },
      template: '<Devices :token="token" />',
    });
  }
}
